import { useToast } from "./ToastContext";
import "../assets/styles/colors.css";

const toastStyles = {
  warning:
    "border-[var(--color-warning)]/40 bg-[color:color-mix(in_srgb,var(--color-bg-card)_88%,var(--color-warning)_12%)]",
  error:
    "border-[var(--color-error)]/40 bg-[color:color-mix(in_srgb,var(--color-bg-card)_88%,var(--color-error)_12%)]",
  success:
    "border-[var(--color-primary)]/40 bg-[color:color-mix(in_srgb,var(--color-bg-card)_88%,var(--color-primary)_12%)]",
  info: "border-[var(--color-primary)]/30 bg-[color:color-mix(in_srgb,var(--color-bg-card)_92%,var(--color-primary)_8%)]",
};

const stripeStyles = {
  warning: "bg-(--color-warning)",
  error: "bg-(--color-error)",
  success: "bg-(--color-primary)",
  info: "bg-(--color-primary)/60",
};

const toastIcons = {
  warning: (
    <svg
      className="w-5 h-5 text-(--color-warning) shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  ),
  error: (
    <svg
      className="w-5 h-5 text-(--color-error) shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
  success: (
    <svg
      className="w-5 h-5 text-(--color-primary) shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  ),
  info: (
    <svg
      className="w-5 h-5 text-(--color-text-secondary) shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
};

function ToastItem({ toast, onDismiss }) {
  const type = toast.type || "info";

  return (
    <div
      style={{ animation: "fadeInUp 0.3s ease-out" }}
      className={`relative w-[min(90vw,520px)] overflow-hidden rounded-2xl border shadow-[0_18px_40px_rgba(15,23,42,0.18)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.6)] backdrop-blur ${toastStyles[type]}`}
    >
      <div
        className={`absolute left-0 top-0 h-full w-1.5 ${stripeStyles[type]}`}
      />
      <div className="flex items-start gap-3 px-4 py-3.5">
        <div className="mt-0.5">{toastIcons[type]}</div>
        <div className="flex-1">
          <p className="text-sm leading-relaxed text-(--color-text-primary)">
            {toast.message}
          </p>
        </div>
        <button
          onClick={() => onDismiss(toast.id)}
          className="shrink-0 rounded-full p-1 text-(--color-text-tertiary) transition-colors hover:bg-(--color-bg-darker) hover:text-(--color-text-primary) dark:hover:bg-(--color-bg-darkest)"
          aria-label="Dismiss"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

/**
 * ToastContainer - Renders all active toasts
 * Place this component once at the root level of your app (e.g., in App.jsx or main.jsx)
 */
function ToastContainer() {
  const { toasts, dismissToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-9999 flex flex-col gap-2 items-end">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={dismissToast} />
      ))}
    </div>
  );
}

export { ToastContainer };
export default ToastContainer;
